import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SidebarLayout } from '../components/layout';
import { Button } from '../components/ui/button';
import { OrderAttributeForm } from '../components/workflows/order-attribute-form';
import { useWorkflowOrderAttributes } from '../hooks/use-order-attributes';
import { useAuth } from '../hooks/use-auth';

export function OrderAttributeDetailPage() {
  const { divisionId, workflowId, fieldId } = useParams<{ divisionId: string; workflowId: string; fieldId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isCreating = fieldId === 'new';

  // Fetch all order attributes to get the specific one for editing
  const { data: fields, isLoading } = useWorkflowOrderAttributes(
    workflowId as string,
    false, // Get all attributes including inactive
    !isCreating && !!workflowId
  );

  const field = !isCreating && fields ? fields.find(f => f.id === fieldId) : null;

  const handleSuccess = () => {
    // Navigate back to workflow edit page with Order Attributes tab selected
    navigate(`/workflows/${divisionId}/${workflowId}/edit?tab=order-attributes`);
  };

  const handleCancel = () => {
    // Navigate back to workflow edit page with Order Attributes tab selected
    navigate(`/workflows/${divisionId}/${workflowId}/edit?tab=order-attributes`);
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="container mx-auto py-8">
          <div className="text-lg text-red-600 dark:text-red-400">Please log in to manage order attributes.</div>
        </div>
      </SidebarLayout>
    );
  }

  if (!isCreating && isLoading) {
    return (
      <SidebarLayout>
        <div className="container mx-auto py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!isCreating && !field) {
    return (
      <SidebarLayout>
        <div className="container mx-auto py-8">
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-8 text-center">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Attribute Not Found</h2>
            <p className="text-red-600 dark:text-red-400 mb-4">
              The order attribute you're looking for doesn't exist or has been deleted.
            </p>
            <Button onClick={() => navigate(`/workflows/${divisionId}/${workflowId}/edit?tab=order-attributes`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Order Attributes
            </Button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="container mx-auto py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/workflows/${divisionId}/${workflowId}/edit?tab=order-attributes`)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Order Attributes
          </Button>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
            {isCreating ? 'Create Order Attribute' : 'Edit Order Attribute'}
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2">
            {isCreating
              ? 'Configure a new order attribute that approvers can edit in order documents'
              : 'Update the configuration for this order attribute'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-8">
          <OrderAttributeForm
            workflowId={workflowId as string}
            field={field}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            isInherited={field?.isInherited}
          />
        </div>
      </div>
    </SidebarLayout>
  );
}
